import { Button } from '@/components/ui/button';
import { X, Trash2, Download, CheckSquare, Square } from 'lucide-react';

interface GallerySelectionBarProps {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onDownload: () => void;
  isDeleting: boolean;
  isDownloading: boolean;
}

export function GallerySelectionBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onDeselectAll,
  onCancel,
  onDelete,
  onDownload,
  isDeleting,
  isDownloading,
}: GallerySelectionBarProps) {
  const allSelected = selectedCount === totalCount && totalCount > 0;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-card border border-border rounded-full shadow-lg px-4 py-2 flex items-center gap-3 animate-in slide-in-from-bottom-4">
      {/* Selection count */}
      <span className="text-sm font-medium text-foreground whitespace-nowrap">
        {selectedCount} selected
      </span>

      <div className="w-px h-6 bg-border" />

      {/* Select all / Deselect all */}
      <Button
        variant="ghost"
        size="sm"
        onClick={allSelected ? onDeselectAll : onSelectAll}
        className="h-8 px-2"
      >
        {allSelected ? (
          <>
            <Square className="h-4 w-4 mr-1" />
            Deselect
          </>
        ) : (
          <>
            <CheckSquare className="h-4 w-4 mr-1" />
            Select All
          </>
        )}
      </Button>

      <div className="w-px h-6 bg-border" />

      {/* Download */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onDownload}
        disabled={selectedCount === 0 || isDownloading || isDeleting}
        className="h-8 px-2"
      >
        <Download className="h-4 w-4 mr-1" />
        {isDownloading ? 'Downloading...' : 'Download'}
      </Button>

      {/* Delete */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onDelete}
        disabled={selectedCount === 0 || isDeleting || isDownloading}
        className="h-8 px-2 text-destructive hover:text-destructive"
      >
        <Trash2 className="h-4 w-4 mr-1" />
        {isDeleting ? 'Deleting...' : 'Delete'}
      </Button>

      <div className="w-px h-6 bg-border" />

      {/* Cancel */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onCancel}
        disabled={isDeleting || isDownloading}
        className="h-8 w-8 p-0"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}